import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

// Enables the locale-aware `L` token (e.g. `06/01/2026` en, `01/06/2026` en-gb).
// `extend` is idempotent, so calling it here is safe even though other modules
// (e.g. the DatePicker) extend the same plugin.
dayjs.extend(localizedFormat)

/**
 * Strict ISO-8601 *date-time* matcher, split in two parts to keep each regex
 * simple. The `T` separator and an `HH:mm` time are required, so plain dates
 * (`2026-06-01`), ids, and other strings never match and are left untouched.
 * Seconds, milliseconds, and a trailing `Z`/`±hh:mm` offset are all optional.
 */
// Fixed-width `YYYY-MM-DDTHH:mm` prefix (always 16 characters)
const ISO_DATE_TIME_PREFIX_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
const ISO_DATE_TIME_PREFIX_LENGTH = 16
// Optional `:ss` / `:ss.fff` straight after the prefix
const ISO_SECONDS_RE = /^:\d{2}(?:\.\d+)?/
// Then only an optional `Z` / `±hh:mm` offset may remain
const ISO_OFFSET_RE = /^(?:Z|[+-]\d{2}:?\d{2})?$/

const isIsoDateTime = (value: string): boolean =>
    ISO_DATE_TIME_PREFIX_RE.test(value) &&
    ISO_OFFSET_RE.test(
        value.slice(ISO_DATE_TIME_PREFIX_LENGTH).replace(ISO_SECONDS_RE, ''),
    )

/**
 * Format a grid cell value for display, collapsing an ISO date-time string to a
 * **date only**, rendered in the user's active locale (dayjs' global locale, set
 * by `useLocale`).
 *
 * Backend timestamps arrive as ISO-8601 strings such as `2026-06-01T08:00:27.258`;
 * list grids only want the calendar date. Anything that isn't an ISO date-time —
 * plain strings, numbers, already date-only values — is returned unchanged (coerced
 * to a string), so this is safe to run over every cell value.
 *
 *   formatDateTimeCell('2026-06-01T08:00:27.258') // → '06/01/2026' (locale 'en')
 *   formatDateTimeCell('2026-06-01')               // → '2026-06-01' (no time → untouched)
 *   formatDateTimeCell('Active')                   // → 'Active'
 *   formatDateTimeCell(42)                          // → '42'
 *   formatDateTimeCell(null)                        // → ''
 */
export const formatDateTimeCell = (value: unknown): string => {
    if (value == null) return ''
    if (typeof value !== 'string') {
        // Narrowed to a non-nullish, non-string value; stringify via its own toString
        const stringable: { toString(): string } = value
        return String(stringable)
    }

    if (!isIsoDateTime(value)) return value

    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('L') : value
}

export default formatDateTimeCell
